import filter from 'lodash/filter';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import flow from 'lodash/fp/flow';
import getOr from 'lodash/fp/getOr';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';

/**
 * Drills into permission solution hierarchy adding properties to each level
 * type : 'solution', 'feature', 'permission'
 * solution : the solution for which a permissions or feature is contained within
 * children : maps solution.feature to children prop or features.permissions to children prop to work with table
 *
 * Creates a Dict of permissions to allow for easier associates to roles values
 *
 * @param solutionResults
 * @returns {{permissionsDict: {}, permissionHierarchy: *}}
 */
export function parseSolutions(solutionResults) {
  const permissionsDict = {};

  const permissionHierarchy = flow([
    getOr([], 'data'),
    (solutions) => sortBy(solutions, [(solution) => solution.name?.toLowerCase()]),
    (solutions) => {
      // map permissions as children to feature and features as children to solutions
      solutions.forEach((solution) => {
        solution.features.forEach((feature) => {
          feature.permissions.forEach((permission) => {
            permissionsDict[permission.id] = permission;
            permission.type = 'permission';
            permission.solution = solution.id;
          });
          feature.children = sortBy(feature.permissions, [(permission) => permission.name?.toLowerCase()]);
          feature.type = 'feature';
          feature.solution = solution.id;
        });
        solution.children = sortBy(solution.features, [(feature) => feature.name?.toLowerCase()]);
        solution.type = 'solution';
        solution.solution = solution.id;
      });
      return solutions;
    },
  ])(solutionResults);

  return {permissionHierarchy, permissionsDict};
}

/**
 * For permissions adds property for each role that has permission enabled
 * this allow for each permission (table row) to have values (enabled/disabled) for each role
 *
 * @param permissionsDict
 * @param roles
 */
export function associateRoleToPermissions(permissionsDict, roles) {
  forEach(permissionsDict, (permission) => {
    roles?.forEach((role) => {
      permission[role.id] = !!find(role.enabledPermissions, {id: permission.id});
    });
  });
}

/**
 * Transforms solutions response and roles response into a permission tree
 * with field values of which roles have access
 *
 * @param solutionResponse
 * @param rolesResponse
 * @returns {{permissionHierarchy: *, roleColumns: *}}
 */
export function parseRolesSolutionResponses(solutionResponse, rolesResponse) {
  const roleColumns = get(rolesResponse, 'data');
  const {permissionHierarchy, permissionsDict} = parseSolutions(solutionResponse);
  associateRoleToPermissions(permissionsDict, roleColumns);

  return {permissionHierarchy, roleColumns};
}

/**
 * Filter out product solutions which are not relevant to roles.
 * At this time the following solutions will be filtered out:
 *  1) The LANGUAGES solution
 *  2) solutions or features with viewable not true
 *
 * @param allSolutions
 * @returns array of solutions
 */
export function roleRestrictedSolutions(allSolutions) {
  return flow([
    (solutions) =>
      filter(solutions, (solution) => {
        return solution.id !== 'LANGUAGES' && solution?.viewable;
      }),
    (solutions) =>
      solutions.map((solution) => {
        solution.features = filter(solution.features, (feature) => feature?.viewable);
        return solution;
      }),
  ])(allSolutions);
}
